var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { UXPContext } from "@core/context/Context";
import { lookupComponent, hasValue, generateUUID } from "@utils";
import { getTheme, defaultGlassLightTheme } from '@theme/themeUtils';
import { WIDGET_PANEL_ID, SPACER_WIDGET_ID, PLACEHOLDER_WIDGET_ID } from './dashboardUtils';
import { Responsive } from "react-grid-layout";
import GridLayout from "react-grid-layout";
import classNames from "classnames";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import DashboardWidget from "./widget/DashboardWidget";
import { syncWidget, removeWidget, updateWidgetProps, processBreakpoints, calculateRowHeight, processInitialWidgetsAndLayouts, updateWidgetCurrentLayout, checkAndAutoConfigureWidget, refreshWidgetAfterAddonInstall, removeDummyWidgets, addPlaceholderWidget, addSpacerWidget } from "./dashboardUtils";
import useToast from "@notifications/Toast/useToast";
import DashboardWidgetConfig from "./widget/DashboardWidgetConfig";
import { useAddOns } from "@helper/AddOn/useAddOns";
/**
 * @export
 */
const Dashboard = ({ widgets: initialWidgets, layouts: initialLayouts, onSave, padding = [10, 10], margin = [10, 10], isEditing, allowRearrange, breakpoints: customBreakpoints, onChangeBreakPoint, showSpacerWidget: showSpacerWidgetProp = false, emptyPlaceholder = { show: false }, allowFreePositioning: allowFreePositioningProp, allowOverlap: allowOverlapProp, isBounded: isBoundedProp = false, transformScale = 1, onContainerMount, breakpointOverride = null, overlayMode = false, disableOverrideDimensions = false, compactType: compactTypeProp = 'vertical', maxRows, widgetPropsOverride, orientation, rows, showGridlines = false, }) => {
    // Apply overlay mode defaults
    const showSpacerWidget = overlayMode ? false : showSpacerWidgetProp;
    const allowFreePositioning = overlayMode ? (allowFreePositioningProp !== null && allowFreePositioningProp !== void 0 ? allowFreePositioningProp : true) : (allowFreePositioningProp !== null && allowFreePositioningProp !== void 0 ? allowFreePositioningProp : false);
    // In overlay mode, overlap follows free positioning (always boolean true/false).
    // In non-overlay, free positioning also enables overlap (otherwise widgets block each other when dragging freely).
    const allowOverlap = overlayMode ? allowFreePositioning : (allowFreePositioning ? true : allowOverlapProp);
    const isBounded = overlayMode ? true : isBoundedProp;
    const context = useContext(UXPContext);
    const toast = useToast();
    const addOns = useAddOns();
    const [widgets, setWidgets] = useState([]);
    const [layouts, setLayouts] = useState({});
    const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [configuringWidget, setConfiguringWidget] = useState(null);
    const [layoutSaveStatus, setLayoutSaveStatus] = useState('idle');
    const widgetsRef = useRef([]);
    const layoutsRef = useRef({});
    const layoutSaveTimerRef = useRef(null);
    const containerRef = useRef(null);
    const allowToConfigure = useMemo(() => (allowRearrange || isEditing), [allowRearrange, isEditing]);
    const { breakpoints, columns, breakpointConfig } = useMemo(() => (processBreakpoints(customBreakpoints)), [customBreakpoints]);
    const rowHeight = useMemo(() => (calculateRowHeight(containerWidth, breakpointOverride || currentBreakpoint, columns, margin)), [containerWidth, breakpointOverride, currentBreakpoint, columns, margin]);
    // Orientation mode derived values
    const isVerticalMode = orientation === 'vertical';
    const isHorizontalMode = orientation === 'horizontal';
    const effectiveRows = isHorizontalMode ? (rows !== null && rows !== void 0 ? rows : 3) : undefined;
    // Horizontal: rowHeight derived from container height (same 1:1 formula, height axis)
    const effectiveRowHeight = useMemo(() => {
        if (isHorizontalMode && containerHeight > 0 && effectiveRows) {
            return Math.max(20, calculateRowHeight(containerHeight, 'default', { default: effectiveRows }, margin));
        }
        return rowHeight;
    }, [isHorizontalMode, containerHeight, effectiveRows, rowHeight, margin]);
    // Horizontal: columns grow from layout content so width expands rightward
    const effectiveCols = useMemo(() => {
        if (!isHorizontalMode || !effectiveRows)
            return null;
        const bp = breakpointOverride || currentBreakpoint;
        const currentLayout = (layouts[bp] || []).filter(i => i.i !== SPACER_WIDGET_ID && i.i !== PLACEHOLDER_WIDGET_ID);
        const maxRight = currentLayout.reduce((max, i) => Math.max(max, i.x + i.w), 0);
        return Math.max(maxRight + effectiveRows, effectiveRows * 3);
    }, [isHorizontalMode, effectiveRows, layouts, breakpointOverride, currentBreakpoint]);
    // Horizontal: width = cols * colWidth + margins (ensures colWidth = rowHeight, 1:1 cells)
    const effectiveGridWidth = useMemo(() => {
        if (!isHorizontalMode || !effectiveCols || !effectiveRowHeight)
            return null;
        return effectiveRowHeight * effectiveCols + margin[0] * (effectiveCols - 1);
    }, [isHorizontalMode, effectiveCols, effectiveRowHeight, margin]);
    // Effective compactType, maxRows, resize handles per orientation
    const effectiveCompactType = isHorizontalMode ? 'horizontal' : isVerticalMode ? 'vertical' : compactTypeProp;
    const effectiveMaxRows = isHorizontalMode ? effectiveRows : maxRows;
    const effectiveResizeHandles = (isVerticalMode ? ['s'] : isHorizontalMode ? ['e', 'w'] : ['sw', 'se']);
    // Normalize item positions/sizes to match orientation constraints
    const normalizeForOrientation = useCallback((items, cols) => {
        if (isVerticalMode)
            return items.map(i => (Object.assign(Object.assign({}, i), { w: cols, x: 0 })));
        if (isHorizontalMode && effectiveRows)
            return items.map(i => (Object.assign(Object.assign({}, i), { h: Math.min(i.h, effectiveRows), y: 0 })));
        return items;
    }, [isVerticalMode, isHorizontalMode, effectiveRows]);
    // Calculate override dimensions when breakpoint is forced
    const overrideDimensions = useMemo(() => {
        if (disableOverrideDimensions)
            return null;
        if (!isEditing || !breakpointOverride || !breakpointConfig[breakpointOverride]) {
            return null;
        }
        const breakpointWidth = breakpointConfig[breakpointOverride].width;
        // Calculate height based on grid rows if bounded
        let height = null;
        if (isBounded && layouts[breakpointOverride]) {
            const layout = layouts[breakpointOverride] || [];
            const maxY = Math.max(...layout.map(l => l.y + l.h), 0);
            const overrideRowHeight = calculateRowHeight(breakpointWidth, breakpointOverride, columns, margin);
            height = (maxY * overrideRowHeight) + ((maxY - 1) * margin[1]);
        }
        return {
            width: breakpointWidth,
            height
        };
    }, [disableOverrideDimensions, breakpointOverride, breakpointConfig, isBounded, layouts, columns, margin, isEditing]);
    useEffect(() => {
        // Adapt stored widget data: old saves use `.widget` (component) and `.props` (configuredProps)
        const adaptedWidgets = initialWidgets.map((w) => {
            var _a, _b, _c, _d, _e;
            return (Object.assign(Object.assign({}, w), { component: (_b = (_a = w.component) !== null && _a !== void 0 ? _a : w.widget) !== null && _b !== void 0 ? _b : null, configuredProps: (_d = (_c = w.configuredProps) !== null && _c !== void 0 ? _c : w.props) !== null && _d !== void 0 ? _d : {}, hasConfigured: (_e = w.hasConfigured) !== null && _e !== void 0 ? _e : false }));
        });
        const { widgets: formattedWidgets, layouts: formattedLayouts } = processInitialWidgetsAndLayouts(adaptedWidgets, initialLayouts, breakpoints, columns, context.direction);
        widgetsRef.current = formattedWidgets;
        // Apply orientation constraints to initial layouts
        if (orientation) {
            layoutsRef.current = Object.fromEntries(Object.entries(formattedLayouts).map(([bp, items]) => {
                const cols = columns[bp] || 30;
                return [bp, normalizeForOrientation(items || [], cols)];
            }));
        }
        else {
            layoutsRef.current = formattedLayouts;
        }
        setWidgets(widgetsRef.current);
        setLayouts(layoutsRef.current);
        /**
         * At this point all widget scripts are loaded.
         * find the registered widgets and update state
         */
        widgetsRef.current = (widgetsRef.current || []).map(widget => {
            const registered = lookupComponent(widget.id);
            if (!!registered) {
                widget.component = registered.component;
                widget.configs = registered.configs;
                widget.isNotAvalable = false;
            }
            else {
                widget.isNotAvalable = true;
            }
            // Check and auto-configure widget if applicable
            widget = checkAndAutoConfigureWidget(widget);
            return widget;
        });
        // Apply dummy widgets if enabled (spacer and/or placeholder)
        if (showSpacerWidget || (emptyPlaceholder === null || emptyPlaceholder === void 0 ? void 0 : emptyPlaceholder.show)) {
            const placeholderConfig = {
                direction: context.direction,
                message: hasValue(emptyPlaceholder === null || emptyPlaceholder === void 0 ? void 0 : emptyPlaceholder.message)
                    ? {
                        enable: true,
                        content: emptyPlaceholder.message
                    }
                    : undefined
            };
            // Only add dummy widgets if appropriate flags are set
            if (widgetsRef.current.length === 0 && (emptyPlaceholder === null || emptyPlaceholder === void 0 ? void 0 : emptyPlaceholder.show)) {
                // Empty dashboard - add placeholder
                const result = addPlaceholderWidget(widgetsRef.current, layoutsRef.current, columns, placeholderConfig);
                widgetsRef.current = result.widgets;
                layoutsRef.current = result.layouts;
            }
            else if (widgetsRef.current.length > 0 && showSpacerWidget) {
                // Dashboard has widgets - add spacer
                const result = addSpacerWidget(widgetsRef.current, layoutsRef.current, columns);
                widgetsRef.current = result.widgets;
                layoutsRef.current = result.layouts;
            }
        }
        setWidgets(widgetsRef.current);
        setLayouts(layoutsRef.current);
    }, [initialWidgets, initialLayouts, breakpoints, columns, context.direction, showSpacerWidget, emptyPlaceholder === null || emptyPlaceholder === void 0 ? void 0 : emptyPlaceholder.show, emptyPlaceholder === null || emptyPlaceholder === void 0 ? void 0 : emptyPlaceholder.message, orientation, normalizeForOrientation]);
    // Measure container dimensions. useLayoutEffect fires synchronously before the browser
    // paints, so containerWidth is set on the first paint — no 0→measured flash.
    useLayoutEffect(() => {
        const updateContainerSize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
                setContainerHeight(containerRef.current.offsetHeight);
            }
        };
        updateContainerSize();
        const resizeObserver = new ResizeObserver(updateContainerSize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    // Apply glass light theme to the container when overlay mode is active
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !overlayMode)
            return;
        const { config } = getTheme(defaultGlassLightTheme, (context === null || context === void 0 ? void 0 : context.customThemes) || {});
        el.classList.add('uxp-glass-theme');
        for (const [key, value] of Object.entries(config)) {
            if (key === 'logo')
                continue;
            el.style.setProperty(`--${key}`, value);
        }
        // Override primaryBGColor to use the glass tint rather than fully transparent.
        // Glass Light sets primaryBGColor = rgba(255,255,255,0) which causes inner widget
        // elements (e.g. uxpcore_radialgauge) to appear as white boxes on light backgrounds.
        // Using secondaryBGColor (the glass tint) gives a uniform glass look throughout.
        // el.style.setProperty('--primaryBGColor', config.secondaryBGColor as string);
        return () => {
            el.classList.remove('uxp-glass-theme');
            for (const key of Object.keys(config)) {
                if (key === 'logo')
                    continue;
                el.style.removeProperty(`--${key}`);
            }
        };
    }, [overlayMode, context === null || context === void 0 ? void 0 : context.customThemes]);
    const handleLayoutChange = (currentLayout) => {
        // In overlay mode, clamp h so widgets can't resize below the container bottom
        if (overlayMode && containerHeight > 0 && rowHeight > 0) {
            const maxRows = Math.floor(containerHeight / (rowHeight + margin[1]));
            currentLayout = currentLayout.map(item => (Object.assign(Object.assign({}, item), { h: Math.max(1, Math.min(item.h, maxRows - item.y)) })));
        }
        // Enforce orientation constraints on every layout change
        if (orientation) {
            const bp = breakpointOverride || currentBreakpoint;
            const cols = columns[bp] || 30;
            currentLayout = normalizeForOrientation(currentLayout, cols);
        }
        // If breakpoint override is active, we're in single-breakpoint editing mode
        if (breakpointOverride) {
            // Update only the specified breakpoint's layout
            layoutsRef.current = Object.assign(Object.assign({}, layoutsRef.current), { [breakpointOverride]: currentLayout });
            console.log(`[Dashboard] Updated layout for breakpoint: ${breakpointOverride}`, currentLayout);
        }
        else {
            // Responsive mode - update all layouts
            layoutsRef.current = Object.keys(layoutsRef.current).reduce((final, breakpoint) => {
                final[breakpoint] = currentLayout;
                return final;
            }, layoutsRef.current);
        }
        setLayouts(layoutsRef.current);
        // update widget's current layout for UI reactions (EX: edit toolbar adjustments, etc)
        const breakpointForUpdate = breakpointOverride || currentBreakpoint;
        widgetsRef.current = updateWidgetCurrentLayout(widgetsRef.current, layoutsRef.current, breakpointForUpdate);
        setWidgets(widgetsRef.current);
        // Remove dummy widgets before saving
        const cleaned = removeDummyWidgets(widgetsRef.current, layoutsRef.current);
        // save changes and show inline feedback
        setLayoutSaveStatus('saving');
        if (layoutSaveTimerRef.current)
            clearTimeout(layoutSaveTimerRef.current);
        onSave(cleaned.widgets, cleaned.layouts).then(success => {
            setLayoutSaveStatus(success ? 'saved' : 'error');
            layoutSaveTimerRef.current = setTimeout(() => setLayoutSaveStatus('idle'), 2500);
        });
    };
    const handleWidgetSync = useCallback((widget) => {
        if (!allowToConfigure)
            return;
        toast.info(context.$L('uxp-core.text.start-sync'));
        const updatedWidgets = syncWidget(widget, widgetsRef.current);
        widgetsRef.current = updatedWidgets;
        setWidgets(updatedWidgets);
        // Remove dummy widgets before saving
        const cleaned = removeDummyWidgets(widgetsRef.current, layoutsRef.current);
        // Save changes
        onSave(cleaned.widgets, cleaned.layouts)
            .then(success => {
            if (success)
                toast.success(context.$L('uxp-core.text.synced'));
            else
                toast.error(context.$L('uxp-core.text.unable-to-sync'));
        });
    }, [onSave, context]);
    const handleWidgetRemove = useCallback((widget) => {
        const { widgets: updatedWidgets, layouts: updatedLayouts } = removeWidget(widget, widgetsRef.current, layoutsRef.current);
        widgetsRef.current = updatedWidgets;
        layoutsRef.current = updatedLayouts;
        setWidgets(updatedWidgets);
        setLayouts(updatedLayouts);
        // Remove dummy widgets before saving
        const cleaned = removeDummyWidgets(updatedWidgets, updatedLayouts);
        // Save changes
        onSave(cleaned.widgets, cleaned.layouts)
            .then(success => {
            if (success)
                toast.success(context.$L('uxp-core.text.widget-removed'));
            else
                toast.error(context.$L('uxp-core.text.unable-to-remove-widget'));
        });
    }, [onSave]);
    const handleWidgetDuplicate = useCallback((widget) => {
        var _a, _b;
        const newId = generateUUID();
        const oldLayout = widget.layout || {};
        const newLayout = Object.assign(Object.assign({}, oldLayout), { i: newId, y: ((_a = oldLayout.y) !== null && _a !== void 0 ? _a : 0) + ((_b = oldLayout.h) !== null && _b !== void 0 ? _b : 4) });
        const newWidget = Object.assign(Object.assign({}, widget), { _id: newId, layout: newLayout });
        const updatedWidgets = [...widgetsRef.current, newWidget];
        const updatedLayouts = {};
        for (const bp of Object.keys(layoutsRef.current)) {
            updatedLayouts[bp] = [...(layoutsRef.current[bp] || []), newLayout];
        }
        widgetsRef.current = updatedWidgets;
        layoutsRef.current = updatedLayouts;
        setWidgets(updatedWidgets);
        setLayouts(updatedLayouts);
        const cleaned = removeDummyWidgets(updatedWidgets, updatedLayouts);
        onSave(cleaned.widgets, cleaned.layouts)
            .then(success => {
            if (success)
                toast.success(context.$L('uxp-core.text.widget-duplicated'));
            else
                toast.error(context.$L('uxp-core.text.unable-to-save'));
        });
    }, [onSave, context, toast]);
    const handleWidgetConfigure = useCallback((widget) => {
        if (!allowToConfigure)
            return;
        setConfiguringWidget(widget);
    }, [allowToConfigure]);
    const handleConfigSave = useCallback((updatedWidget) => {
        // Update the widget in the widgets array
        const updatedWidgets = widgetsRef.current.map(w => w._id === updatedWidget._id ? updatedWidget : w);
        widgetsRef.current = updatedWidgets;
        setWidgets(widgetsRef.current);
        // Close the config modal
        setConfiguringWidget(null);
        // Remove dummy widgets before saving
        const cleaned = removeDummyWidgets(widgetsRef.current, layoutsRef.current);
        // Save changes
        onSave(cleaned.widgets, cleaned.layouts)
            .then(success => {
            if (success)
                toast.success(context.$L('uxp-core.text.changes-saved'));
            else
                toast.error(context.$L('uxp-core.text.unable-to-save'));
        });
    }, [onSave, context, toast]);
    const handleWidgetPropsChange = useCallback((instanceId, props) => {
        const updatedWidgets = updateWidgetProps(instanceId, props, widgetsRef.current);
        widgetsRef.current = updatedWidgets;
        setWidgets(updatedWidgets);
        // Remove dummy widgets before saving
        const cleaned = removeDummyWidgets(widgetsRef.current, layoutsRef.current);
        // Find the widget to check if it's a container — container widgets (e.g. WidgetPanel)
        // propagate their own child-state changes via onWidgetPropsChange, so toasts would be
        // misleading noise for internal saves that the user didn't directly trigger.
        const widget = widgetsRef.current.find(w => w._id === instanceId);
        const silent = (widget === null || widget === void 0 ? void 0 : widget.id) === WIDGET_PANEL_ID;
        onSave(cleaned.widgets, cleaned.layouts)
            .then(success => {
            if (silent)
                return;
            if (success)
                toast.success(context.$L('uxp-core.text.changes-saved'));
            else
                toast.error(context.$L('uxp-core.text.unable-to-save'));
        });
    }, []);
    const handleWidgetLock = useCallback((widget) => {
        var _a;
        if (!allowToConfigure)
            return;
        const breakpoint = breakpointOverride || currentBreakpoint;
        const isCurrentlyLocked = ((_a = widget.layout) === null || _a === void 0 ? void 0 : _a.isDraggable) === false;
        // Toggle isDraggable/isResizable instead of static to avoid react-grid-layout
        // repositioning the item during its internal layout recalculation
        const updatedLayouts = Object.fromEntries(Object.entries(layoutsRef.current).map(([bp, items]) => [bp,
            (items || []).map(item => item.i === widget._id
                ? Object.assign(Object.assign({}, item), { isDraggable: isCurrentlyLocked, isResizable: isCurrentlyLocked }) : item)
        ]));
        layoutsRef.current = updatedLayouts;
        setLayouts(updatedLayouts);
        // Sync widget.layout.static so the toolbar icon reflects the new state
        widgetsRef.current = updateWidgetCurrentLayout(widgetsRef.current, updatedLayouts, breakpoint);
        setWidgets(widgetsRef.current);
        const cleaned = removeDummyWidgets(widgetsRef.current, layoutsRef.current);
        onSave(cleaned.widgets, cleaned.layouts);
    }, [allowToConfigure, breakpointOverride, currentBreakpoint, onSave]);
    const handleAddonInstalled = useCallback((widget) => __awaiter(void 0, void 0, void 0, function* () {
        // Refresh widget after addon installation/configuration
        const refreshedWidget = yield refreshWidgetAfterAddonInstall(widget, addOns);
        // Update the widget in the widgets array
        const updatedWidgets = widgetsRef.current.map(w => w._id === refreshedWidget._id ? refreshedWidget : w);
        widgetsRef.current = updatedWidgets;
        setWidgets(updatedWidgets);
        // Remove dummy widgets before saving
        const cleaned = removeDummyWidgets(widgetsRef.current, layoutsRef.current);
        // Save changes silently - InstallAddOn component handles user feedback
        yield onSave(cleaned.widgets, cleaned.layouts);
    }), [addOns, onSave]);
    const renderWidget = useCallback((widget) => {
        const instanceId = widget._id;
        // Merge runtime context (URL params, event payloads) at render time so that
        // changes to widgetPropsOverride never trigger the init useEffect.
        const widgetWithOverrides = widgetPropsOverride && Object.keys(widgetPropsOverride).length > 0
            ? Object.assign(Object.assign({}, widget), { configuredProps: Object.assign(Object.assign({}, (widget.configuredProps || {})), widgetPropsOverride) }) : widget;
        return _jsx("div", { className: "uxpcore_dashboard__layout-item", dir: (context === null || context === void 0 ? void 0 : context.direction) || 'ltr', "data-instance-id": instanceId, children: _jsx("div", { className: "uxpcore_dashboard__layout-content", id: `widget-layout-${instanceId}`, children: _jsx(ErrorBoundary, { children: _jsx(DashboardWidget, { widget: widgetWithOverrides, onPropsChange: (props) => handleWidgetPropsChange(instanceId, props), isEditing: isEditing, onSync: handleWidgetSync, onRemove: handleWidgetRemove, onDuplicate: handleWidgetDuplicate, onConfigure: handleWidgetConfigure, onLock: handleWidgetLock, onAddonInstalled: handleAddonInstalled, onPlaceholderClick: emptyPlaceholder === null || emptyPlaceholder === void 0 ? void 0 : emptyPlaceholder.onClick }) }) }) }, instanceId);
    }, [isEditing, handleWidgetSync, handleWidgetRemove, handleWidgetDuplicate, handleWidgetPropsChange, handleWidgetLock, handleAddonInstalled, emptyPlaceholder, widgetPropsOverride]);
    const colWidth = useMemo(() => {
        if (!isEditing || !showGridlines || containerWidth <= 0)
            return null;
        const bp = breakpointOverride || currentBreakpoint;
        const cols = isHorizontalMode && effectiveCols ? effectiveCols : (columns[bp] || 30);
        return (containerWidth - margin[0] * (cols - 1) - padding[0] * 2) / cols;
    }, [isEditing, showGridlines, containerWidth, breakpointOverride, currentBreakpoint, isHorizontalMode, effectiveCols, columns, margin, padding]);
    const styles = Object.assign(Object.assign(Object.assign({ '--layoutItemPadding': `${padding[0]}px ${padding[1]}px` }, (isHorizontalMode && effectiveGridWidth ? { minWidth: `${effectiveGridWidth}px` } : {})), (isEditing && showGridlines && colWidth !== null ? {
        '--gridCellWidth': `${colWidth}px`,
        '--gridCellHeight': `${effectiveRowHeight}px`,
        '--gridPaddingX': `${padding[0]}px`,
        '--gridPaddingY': `${padding[1]}px`,
        '--gridMarginX': `${margin[0]}px`,
        '--gridMarginY': `${margin[1]}px`,
    } : {})), (overrideDimensions !== null ? Object.assign({ width: `${overrideDimensions.width}px`, maxWidth: `${overrideDimensions.width}px` }, (overrideDimensions.height !== null ? {
        height: `${overrideDimensions.height}px`,
        maxHeight: `${overrideDimensions.height}px`
    } : {})) : {}));
    // If allowOverlap is explicitly set, use it; otherwise fall back to isBounded
    const allowWidgetOverlap = useMemo(() => allowOverlap !== undefined ? allowOverlap : isBounded, [allowOverlap, isBounded]);
    const preventCollision = useMemo(() => (allowFreePositioning || allowOverlap), [allowFreePositioning, allowOverlap]);
    return _jsxs(_Fragment, { children: [_jsx("div", { className: classNames('uxpcore_dashboard__wrapper', {
                    'uxpcore_dashboard__wrapper--scroll': overrideDimensions !== null,
                    'uxpcore_dashboard__wrapper--edit': isEditing,
                }), children: _jsx("div", { ref: (el) => {
                        containerRef.current = el;
                        if (el)
                            onContainerMount === null || onContainerMount === void 0 ? void 0 : onContainerMount(el);
                    }, className: classNames('uxpcore_dashboard__container', {
                        'uxpcore_dashboard__container--edit': isEditing,
                        'uxpcore_dashboard__container--bounded': isBounded,
                        'uxpcore_dashboard__container--breakpoint-override': overrideDimensions !== null,
                        'uxpcore_dashboard__container--overlay': overlayMode,
                        'uxpcore_dashboard__container--horizontal': isHorizontalMode,
                        'uxpcore_dashboard__container--vertical': isVerticalMode,
                        'uxpcore_dashboard__container--gridlines': isEditing && showGridlines && colWidth !== null,
                    }), dir: "ltr", style: styles, children: breakpointOverride ? (
                    // Single-breakpoint editing mode - use regular GridLayout
                    _jsx(GridLayout, { layout: layouts[breakpointOverride] || [], cols: isHorizontalMode && effectiveCols ? effectiveCols : (columns[breakpointOverride] || 30), rowHeight: effectiveRowHeight, width: effectiveGridWidth !== null && effectiveGridWidth !== void 0 ? effectiveGridWidth : ((overrideDimensions === null || overrideDimensions === void 0 ? void 0 : overrideDimensions.width) || containerWidth), margin: margin, isDraggable: allowToConfigure, isResizable: allowToConfigure, allowOverlap: allowWidgetOverlap, preventCollision: preventCollision, isBounded: isBounded, compactType: preventCollision ? null : effectiveCompactType, maxRows: effectiveMaxRows, className: "uxpcore_dashboard__layout", draggableHandle: ".uxp-drag-handler", resizeHandles: effectiveResizeHandles, onDragStop: (layouts) => handleLayoutChange(layouts), onResizeStop: (layouts) => handleLayoutChange(layouts), transformScale: transformScale, children: widgets.map(w => renderWidget(w)) })) : (
                    // Responsive mode — pass measured width directly so there is a single
                    // source of truth for the container width. WidthProvider would add a
                    // second measurement that fires asynchronously after mount, causing
                    // 2-3 visible layout iterations before the breakpoint settles.
                    containerWidth > 0 && _jsx(Responsive, { width: containerWidth, layouts: layouts, breakpoints: breakpoints, cols: columns, rowHeight: effectiveRowHeight, margin: margin, isDraggable: allowToConfigure, isResizable: allowToConfigure, allowOverlap: allowWidgetOverlap, preventCollision: preventCollision, isBounded: isBounded, compactType: preventCollision ? null : effectiveCompactType, maxRows: effectiveMaxRows, className: "uxpcore_dashboard__layout", draggableHandle: ".uxp-drag-handler", resizeHandles: effectiveResizeHandles, onBreakpointChange: (breakpoint, cols) => {
                            setCurrentBreakpoint(breakpoint);
                            onChangeBreakPoint === null || onChangeBreakPoint === void 0 ? void 0 : onChangeBreakPoint(cols);
                        }, onDragStop: (layouts) => handleLayoutChange(layouts), onResizeStop: (layouts) => handleLayoutChange(layouts), transformScale: transformScale, children: widgets.map(w => renderWidget(w)) })) }) }), _jsx(DashboardWidgetConfig, { widget: configuringWidget, onClose: () => setConfiguringWidget(null), onSave: handleConfigSave }), isEditing && layoutSaveStatus !== 'idle' && (_jsxs("div", { className: classNames('uxpcore_dashboard__save-status', {
                    'uxpcore_dashboard__save-status--saving': layoutSaveStatus === 'saving',
                    'uxpcore_dashboard__save-status--saved': layoutSaveStatus === 'saved',
                    'uxpcore_dashboard__save-status--error': layoutSaveStatus === 'error',
                }), children: [layoutSaveStatus === 'saving' && (_jsxs(_Fragment, { children: [_jsx("span", { className: "uxpcore_dashboard__save-status-spinner" }), context.$L('uxp-core.text.saving-changes')] })), layoutSaveStatus === 'saved' && (_jsxs(_Fragment, { children: ["\u2713 ", context.$L('uxp-core.text.changes-saved')] })), layoutSaveStatus === 'error' && (_jsxs(_Fragment, { children: ["\u2715 ", context.$L('uxp-core.text.save-failed')] }))] }))] });
};
export default Dashboard;
